/**
 * «Avisar de un problema aquí» (Vadik, 31-oct-2026): la foto de la pantalla
 * que acompaña al aviso. Se hace en el móvil y no sale de él hasta que la
 * persona pulsa «Enviar con la foto». Las cifras se tapan de serie: antes de
 * la foto se apunta dónde está cada cifra y después se pintan encima unas
 * pastillas grises.
 */
import { toCanvas } from 'html-to-image';

/** Rectángulo en píxeles CSS de la ventana. */
export type Caja = { x: number; y: number; w: number; h: number };

export type FotoPantalla = {
  lienzo: HTMLCanvasElement;
  /** Dónde había cifras (píxeles CSS de la ventana). */
  cifras: Caja[];
  /** Píxeles del lienzo por píxel CSS. */
  escala: number;
};

/** Atributo de lo que no sale en la foto (el destello, la propia hoja). */
export const SIN_FOTO = 'data-sin-foto';

/** Tope de la foto ya en JPEG (el servidor acepta hasta 600 kB). */
export const MAX_FOTO_BYTES = 550 * 1024;

/**
 * Tramos [inicio, fin) del texto con cifras: «1.234,56», «-75», «2026»,
 * «4111 1111». Un número suelto también cuenta (puede ser un importe).
 */
export function tramosCifras(texto: string): Array<[number, number]> {
  const tramos: Array<[number, number]> = [];
  for (const m of texto.matchAll(/\d(?:[\d.,'’  ]*\d)?/g)) {
    const inicio = m.index ?? 0;
    tramos.push([inicio, inicio + m[0].length]);
  }
  return tramos;
}

/** Móvil y navegador, en corto («Android · Chrome 154»), sin más detalle. */
export function aparatoDe(ua: string): string {
  const sistema = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /Linux/.test(ua)
              ? 'Linux'
              : null;
  const navegadores: Array<[string, RegExp]> = [
    ['Samsung Internet', /SamsungBrowser\/(\d+)/],
    ['Edge', /Edg(?:A|iOS)?\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari/],
  ];
  let navegador: string | null = null;
  for (const [nombre, patron] of navegadores) {
    const m = patron.exec(ua);
    if (m) {
      navegador = `${nombre} ${m[1]}`;
      break;
    }
  }
  return [sistema, navegador].filter(Boolean).join(' · ') || '?';
}

/**
 * ¿Se ve de verdad ese trozo? Lo que queda fuera de una lista con scroll o
 * debajo de una cabecera fija sigue teniendo su caja, pero no sale en la
 * foto: no hace falta taparlo (y taparlo taparía lo que sí se ve encima).
 */
function seVe(padre: Element, r: DOMRect): boolean {
  if (typeof document.elementFromPoint !== 'function') {
    return true;
  }
  const encima = document.elementFromPoint(
    Math.min(Math.max(r.left + r.width / 2, 0), window.innerWidth - 1),
    Math.min(Math.max(r.top + r.height / 2, 0), window.innerHeight - 1),
  );
  return (
    encima == null ||
    padre === encima ||
    padre.contains(encima) ||
    encima.contains(padre)
  );
}

/** Cajas de las cifras visibles en la ventana (antes de hacer la foto). */
export function cajasCifras(raiz: Node = document.body): Caja[] {
  const cajas: Caja[] = [];
  const ancho = window.innerWidth;
  const alto = window.innerHeight;
  const recorrido = document.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
  for (let n = recorrido.nextNode(); n; n = recorrido.nextNode()) {
    const valor = n.nodeValue ?? '';
    const padre = n.parentElement;
    if (!/\d/.test(valor) || !padre || padre.closest(`[${SIN_FOTO}]`)) {
      continue;
    }
    for (const [inicio, fin] of tramosCifras(valor)) {
      const rango = document.createRange();
      rango.setStart(n, inicio);
      rango.setEnd(n, fin);
      for (const r of rango.getClientRects()) {
        if (
          r.width > 0 &&
          r.height > 0 &&
          r.right > 0 &&
          r.bottom > 0 &&
          r.left < ancho &&
          r.top < alto &&
          seVe(padre, r)
        ) {
          cajas.push({ x: r.left, y: r.top, w: r.width, h: r.height });
        }
      }
    }
  }
  // Campos de texto: su valor no es un nodo de texto; se tapan enteros.
  for (const campo of document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement
  >('input, textarea')) {
    if (/\d/.test(campo.value) && !campo.closest(`[${SIN_FOTO}]`)) {
      const r = campo.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        cajas.push({ x: r.left, y: r.top, w: r.width, h: r.height });
      }
    }
  }
  return cajas;
}

/**
 * La copia de la página que usa html-to-image empieza siempre arriba en cada
 * lista con scroll. Mientras se hace la foto (tapada por el destello), cada
 * lista desplazada mueve sus hijos lo mismo que su scroll; luego todo vuelve
 * a como estaba.
 */
function fijarScroll(): () => void {
  const deshacer: Array<() => void> = [];
  for (const el of document.body.querySelectorAll<HTMLElement>('*')) {
    const { scrollTop, scrollLeft } = el;
    if ((scrollTop === 0 && scrollLeft === 0) || el.closest(`[${SIN_FOTO}]`)) {
      continue;
    }
    for (const hijo of el.children) {
      if (!(hijo instanceof HTMLElement || hijo instanceof SVGElement)) {
        continue;
      }
      const antes = hijo.style.transform;
      const calculado = getComputedStyle(hijo).transform;
      hijo.style.transform = `translate(${-scrollLeft}px, ${-scrollTop}px)${
        calculado && calculado !== 'none' ? ` ${calculado}` : ''
      }`;
      deshacer.push(() => {
        hijo.style.transform = antes;
      });
    }
  }
  return () => deshacer.forEach(f => f());
}

/** Hace la foto de lo que se ve ahora (sin lo marcado con SIN_FOTO). */
export async function hacerFoto(): Promise<FotoPantalla> {
  const escala = Math.min(window.devicePixelRatio || 1, 1.5);
  const cifras = cajasCifras();
  const fondo = getComputedStyle(document.body).backgroundColor;
  const opciones = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: escala,
    backgroundColor: fondo && fondo !== 'rgba(0, 0, 0, 0)' ? fondo : '#ffffff',
    cacheBust: false,
    filter: (nodo: HTMLElement) =>
      !(nodo instanceof Element && nodo.hasAttribute(SIN_FOTO)),
  };
  const volver = fijarScroll();
  try {
    let lienzo: HTMLCanvasElement;
    try {
      lienzo = await toCanvas(document.body, opciones);
    } catch {
      // Sin las fuentes de la app (p. ej. si no se pueden leer): mejor una
      // foto con otra letra que ninguna.
      lienzo = await toCanvas(document.body, { ...opciones, skipFonts: true });
    }
    return { lienzo, cifras, escala };
  } finally {
    volver();
  }
}

/**
 * La foto en JPEG (data URL), con las cifras tapadas si `tapar`. Baja la
 * calidad hasta que cabe en MAX_FOTO_BYTES.
 */
export function fotoJpeg(foto: FotoPantalla, tapar: boolean): string {
  const { lienzo, cifras, escala } = foto;
  const copia = document.createElement('canvas');
  copia.width = lienzo.width;
  copia.height = lienzo.height;
  const ctx = copia.getContext('2d');
  if (!ctx) {
    return lienzo.toDataURL('image/jpeg', 0.7);
  }
  ctx.drawImage(lienzo, 0, 0);
  if (tapar) {
    ctx.fillStyle = '#c4cad6';
    for (const c of cifras) {
      const margen = 2;
      const x = (c.x - margen) * escala;
      const y = (c.y - margen) * escala;
      const w = (c.w + margen * 2) * escala;
      const h = (c.h + margen * 2) * escala;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, Math.min(h / 2, 8 * escala));
      ctx.fill();
    }
  }
  for (const calidad of [0.72, 0.55, 0.4, 0.28]) {
    const url = copia.toDataURL('image/jpeg', calidad);
    if (url.length * 0.75 <= MAX_FOTO_BYTES) {
      return url;
    }
  }
  // Muy grande incluso así: a mitad de tamaño.
  const media = document.createElement('canvas');
  media.width = Math.round(copia.width / 2);
  media.height = Math.round(copia.height / 2);
  media.getContext('2d')?.drawImage(copia, 0, 0, media.width, media.height);
  return media.toDataURL('image/jpeg', 0.5);
}
